/**
 * @file RedirectProbeDialog.tsx
 * @description «Куда ведёт сейчас» — окно «Проверить» у переадресации
 * (phase19.16, партия Р3в, v0.19.64; SB132; db-design-r3v §4): сервер
 * спрашивает адрес у витрины и показывает «ожидаемо» (по правилам) и
 * «получено» (фактический ответ). Совпало — ✓; нет — подсказка, что правило
 * начинает действовать в течение минуты. Сбой витрины — «Проверить не
 * удалось — сайт работает, попробуйте позже [Повторить]».
 */

import { CheckCircle2, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ListError } from "../../ui/ListStates";
import { Skeleton } from "../../ui/skeleton";
import { siteRedirects, type SiteRedirectProbeResult } from "../../../lib/api-site-redirects";
import { observedLine, probeLine, probeMatches } from "../../../lib/site-seo/redirect-probe";
import { seoErrorText } from "../../../lib/site-seo/seo-errors";

interface Props {
  /** Адрес для проверки; `null` — окно закрыто. */
  path: string | null;
  onClose: () => void;
}

export function RedirectProbeDialog({ path, onClose }: Props) {
  const [result, setResult] = useState<SiteRedirectProbeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (target: string) => {
    setResult(null);
    setError(null);
    try {
      setResult(await siteRedirects.probe(target));
    } catch (err) {
      setError(seoErrorText(err, "Проверить не удалось — сайт работает, попробуйте позже."));
    }
  }, []);

  useEffect(() => {
    if (path) void run(path);
  }, [path, run]);

  const matches = result ? probeMatches(result) : null;
  return (
    <Dialog open={path !== null} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[30rem]">
        <DialogHeader>
          <DialogTitle>Куда ведёт {path}</DialogTitle>
          <DialogDescription>Спросили у сайта прямо сейчас.</DialogDescription>
        </DialogHeader>
        {error ? (
          <ListError message={error} onRetry={() => path && void run(path)} />
        ) : !result ? (
          <Skeleton className="h-20 rounded-lg" aria-label="Проверяем адрес" />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="type-body text-on-surface">
              <span className="text-on-surface-variant">Ожидаемо: </span>
              {probeLine(result.expected.status, result.expected.location)}
            </p>
            <p className="type-body text-on-surface">
              <span className="text-on-surface-variant">Получено: </span>
              {observedLine(result)}
            </p>
            {matches === true ? (
              <p className="type-body flex items-center gap-2 text-success">
                <CheckCircle2 aria-hidden className="size-4 shrink-0" />
                Сайт отвечает как ожидалось.
              </p>
            ) : matches === false ? (
              <p className="type-body flex items-start gap-2 text-warning">
                <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
                Ответ отличается. Новое правило начинает действовать в течение минуты — проверьте ещё раз чуть позже.
              </p>
            ) : null}
            {result.note ? <p className="type-caption text-on-surface-variant">{result.note}</p> : null}
            <Button variant="outline" className="self-start max-sm:w-full" onClick={() => path && void run(path)}>
              Проверить снова
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
